// Client view of server-owned passengers: a mini caveman waving at his cave
// mouth with a speech bubble ("UGH! → 5") that reddens as he loses patience,
// a real climb-aboard animation onto the heli, and a hop down + walk into the
// cave on delivery.
import {
  MeshBuilder, StandardMaterial, Color3, Mesh, DynamicTexture, Vector3,
} from '@babylonjs/core';
import { buildCaveman, animateWave, animateSit, animateWalk } from './caveman.js';
import { CAVES, PAD_R } from '../shared/level.mjs';

const clamp01 = v => Math.max(0, Math.min(1, v));

// Four impatience steps × eight destinations = at most 32 bubble textures for
// the whole session, shared by every customer. Never one per spawn.
const BUBBLE_STEPS = [
  { fill: '#fffbe8', ink: '#3d2f1f', edge: '#5a4632' },
  { fill: '#fff2cf', ink: '#6b4a10', edge: '#8a6a20' },
  { fill: '#ffdcb0', ink: '#7d3b0c', edge: '#a4500f' },
  { fill: '#ffc2b0', ink: '#8c1c0c', edge: '#b32410' },
];
const bubbleCache = new Map(); // `${dest}|${step}` -> StandardMaterial

function bubbleMaterial(scene, dest, step) {
  const key = `${dest}|${step}`;
  const hit = bubbleCache.get(key);
  if (hit && !hit.isDisposed?.()) return hit;
  const s = BUBBLE_STEPS[step] || BUBBLE_STEPS[0];
  const dt = new DynamicTexture('bub' + key, { width: 512, height: 256 }, scene, true);
  const c = dt.getContext();
  c.clearRect(0, 0, 512, 256);
  // rounded speech bubble
  c.fillStyle = s.fill; c.strokeStyle = s.edge; c.lineWidth = 10;
  const r = 40;
  c.beginPath();
  c.moveTo(40 + r, 20);
  c.arcTo(472, 20, 472, 196, r); c.arcTo(472, 196, 40, 196, r);
  c.arcTo(40, 196, 40, 20, r); c.arcTo(40, 20, 472, 20, r);
  c.closePath(); c.fill(); c.stroke();
  c.beginPath(); c.moveTo(230, 192); c.lineTo(256, 250); c.lineTo(300, 192); c.closePath();
  c.fillStyle = s.fill; c.fill(); c.stroke();
  c.fillStyle = s.ink; c.font = 'bold 110px Georgia';
  c.textAlign = 'center'; c.textBaseline = 'middle';
  c.fillText(step >= 3 ? `UGH!! ➔ ${dest}` : `UGH! ➔ ${dest}`, 256, 110);
  dt.update();
  dt.hasAlpha = true;
  const m = new StandardMaterial('bubbleM' + key, scene);
  m.diffuseTexture = dt; m.emissiveColor = new Color3(0.9, 0.9, 0.85);
  m.useAlphaFromDiffuseTexture = true; m.specularColor = Color3.Black();
  m.backFaceCulling = false;
  bubbleCache.set(key, m);
  return m;
}

function bubblePlane(scene, dest, step) {
  const plane = MeshBuilder.CreatePlane('bubble', { width: 5.6, height: 2.8 }, scene);
  plane.material = bubbleMaterial(scene, dest, step);
  plane.billboardMode = Mesh.BILLBOARDMODE_ALL;
  plane.isPickable = false;
  return plane;
}

const stepFor = urg => (urg < 0.5 ? 0 : urg < 0.75 ? 1 : urg < 0.92 ? 2 : 3);

export class Passengers {
  constructor(scene) {
    this.scene = scene;
    this.map = new Map(); // paxId -> {data, cm, bubble, state, ...}
    // ONE shared boarding ring for the whole jungle, not one per passenger
    this.ring = null;
    this.ringMat = null;
    this.boardId = null;   // pax currently being boarded
    this.boardK = 0;       // 0..1 progress
    this.boardShown = 0;   // what the ring is actually drawing (collapses on abort)
    this.boardPos = new Vector3(0, 0, 0);
  }

  spawn(pax) {
    const cave = CAVES.find(c => c.id === pax.cave);
    if (!cave || this.map.has(pax.id)) return;
    const cm = buildCaveman(this.scene, { hue: pax.hue ?? 120, scale: 0.95 });
    const fwd = new Vector3(Math.sin(cave.face), 0, Math.cos(cave.face));
    // stands between cave mouth and pad center
    const p = new Vector3(cave.x, cave.y, cave.z).subtract(fwd.scale(PAD_R * 0.55));
    cm.root.position.copyFrom(p);
    cm.root.rotation.y = cave.face;
    cm.baseY = p.y;
    const bubble = bubblePlane(this.scene, pax.dest, 0);
    bubble.position.set(p.x, p.y + 2.6, p.z);
    this.map.set(pax.id, {
      data: pax, cm, bubble, cave, state: 'waiting', step: 0,
      // the server sends the REMAINING patience, so a mid-game joiner sees the
      // same countdown; deadline is stamped on the first tick with a clock
      patience: pax.patience || 60_000,
      left: pax.left !== undefined ? pax.left : (pax.patience || 60_000),
      deadline: null,
      boardT: 0, hopT: 0, leaveT: 0,
    });
  }

  // ---- boarding progress ring (shared mesh, driven from taxiUpdate) ------
  ensureRing() {
    if (this.ring) return;
    const d = MeshBuilder.CreateDisc('boardRing', { radius: 1.6, tessellation: 24 }, this.scene);
    d.rotation.x = Math.PI / 2;
    d.isPickable = false;
    const m = new StandardMaterial('boardRingM', this.scene);
    m.diffuseColor = Color3.Black();
    m.emissiveColor = Color3.FromHexString('#e8c020');
    m.specularColor = Color3.Black();
    m.alpha = 0.6; m.backFaceCulling = false;
    d.material = m;
    d.setEnabled(false);
    this.ring = d; this.ringMat = m;
  }

  setBoardProgress(paxId, k) {
    const e = this.map.get(paxId);
    if (!e) { this.clearBoardProgress(); return; }
    this.ensureRing();
    this.boardId = paxId;
    this.boardK = clamp01(k);
    this.boardPos.copyFrom(e.cm.root.position);
  }

  clearBoardProgress(paxId) {
    if (paxId !== undefined && this.boardId !== paxId) return;
    this.boardId = null;
  }

  setBoarded(paxId, heliParts) {
    const e = this.map.get(paxId);
    if (!e) return;
    this.clearBoardProgress(paxId);
    e.state = 'boarding';
    e.boardT = 0;
    if (e.bubble) { e.bubble.dispose(); e.bubble = null; }
    e.cm.root.parent = heliParts.paxSeat;
    e.cm.root.position.set(1.1, -1.2, 0.6);   // one foot on the skid
    e.cm.root.rotation.y = 1.2;
    e.cm.armR.rotation.z = 0;                 // stop waving, grab the frame
  }

  // passenger hops down, waves and walks into the cave (delivered or gave up)
  dismiss(paxId, atCaveId) {
    const e = this.map.get(paxId);
    if (!e) return;
    const cave = CAVES.find(c => c.id === atCaveId) || CAVES.find(c => c.id === e.data.cave);
    if (e.bubble) { e.bubble.dispose(); e.bubble = null; }
    this.clearBoardProgress(paxId);
    if (!cave) { this.remove(paxId); return; }
    // absolute position must be read BEFORE we cut him loose from the heli
    const from = e.cm.root.getAbsolutePosition().clone();
    e.cm.root.parent = null;
    e.cm.root.position.copyFrom(from);
    const fwd = new Vector3(Math.sin(cave.face), 0, Math.cos(cave.face));
    const land = new Vector3(cave.x, cave.y, cave.z).subtract(fwd.scale(PAD_R * 0.45));
    e.cave = cave;
    e.state = 'hop';
    e.hopFrom = from; e.hopTo = land; e.hopT = 0;
    e.leaveFace = cave.face + Math.PI;   // walk into the mountain
    e.leaveDir = fwd.scale(-1);
    e.leaveT = 0;
    e.cm.baseY = land.y;
  }

  remove(paxId) {
    const e = this.map.get(paxId);
    if (!e) return;
    this.clearBoardProgress(paxId);
    if (e.bubble) e.bubble.dispose();   // the material/texture stays in the cache
    e.cm.root.dispose();
    this.map.delete(paxId);
  }

  // srvNowMs: shared server clock in ms. Without it nobody is impatient.
  update(t, dt, srvNowMs) {
    // shared boarding ring
    if (this.ring) {
      if (this.boardId !== null) this.boardShown = this.boardK;
      else this.boardShown = Math.max(0, this.boardShown - dt / 0.2);
      const on = this.boardShown > 0.002;
      this.ring.setEnabled(on);
      if (on) {
        const k = this.boardShown;
        this.ring.position.set(this.boardPos.x, this.boardPos.y + 0.08, this.boardPos.z);
        this.ring.scaling.setAll(0.2 + 0.8 * k);
        this.ringMat.emissiveColor.set(0.91 - 0.6 * k, 0.75 + 0.1 * k, 0.13 + 0.1 * k);
        this.ringMat.alpha = 0.6;
      }
    }

    for (const [id, e] of this.map) {
      if (e.state === 'waiting') {
        if (e.deadline === null && srvNowMs) e.deadline = srvNowMs + e.left;
        const urg = (e.deadline && srvNowMs)
          ? clamp01(1 - (e.deadline - srvNowMs) / e.patience)
          : 0;
        animateWave(e.cm, t + id * 1.7, urg);
        // he shifts his weight and turns more the closer he is to giving up
        e.cm.root.rotation.y = e.cave.face + Math.sin(t * (1.5 + urg * 3)) * urg * 0.35;
        const step = stepFor(urg);
        if (step !== e.step && e.bubble) {
          e.step = step;
          e.bubble.material = bubbleMaterial(this.scene, e.data.dest, step);
        }
        if (e.bubble) {
          e.bubble.position.y = e.cm.baseY + 2.6 + Math.sin(t * 2 + id) * 0.15;
          const pulse = step >= 3 ? 1 + Math.sin(t * 9) * 0.06 : 1;
          e.bubble.scaling.setAll(pulse);
        }
      } else if (e.state === 'boarding') {
        e.boardT += dt;
        const k = Math.min(1, e.boardT / 0.45);
        const inv = 1 - k;
        e.cm.root.position.set(1.1 * inv, -1.2 * inv + Math.sin(k * Math.PI) * 0.5, 0.6 * inv);
        e.cm.root.rotation.y = 1.2 * inv;
        e.cm.armL.rotation.x = -1.4 * inv;
        e.cm.armR.rotation.x = -1.4 * inv;
        e.cm.legL.rotation.x = 1.2 * k;
        e.cm.legR.rotation.x = 1.2 * k;
        if (k >= 1) { e.state = 'boarded'; animateSit(e.cm); }
      } else if (e.state === 'boarded') {
        // riding along: a small bob and a head that looks around
        e.cm.root.position.set(0, Math.sin(t * 2.2) * 0.03, 0);
        e.cm.head.rotation.y = Math.sin(t * 0.6) * 0.5;
      } else if (e.state === 'hop') {
        e.hopT += dt;
        const k = Math.min(1, e.hopT / 0.35);
        e.cm.root.position.set(
          e.hopFrom.x + (e.hopTo.x - e.hopFrom.x) * k,
          e.hopFrom.y + (e.hopTo.y - e.hopFrom.y) * k + Math.sin(k * Math.PI) * 0.6,
          e.hopFrom.z + (e.hopTo.z - e.hopFrom.z) * k,
        );
        animateWalk(e.cm, t, 1.4);
        if (k >= 1) { e.state = 'leaving'; e.leaveT = 0; e.cm.root.rotation.y = e.leaveFace; }
      } else if (e.state === 'leaving') {
        e.leaveT += dt;
        if (e.leaveT < 0.5) {
          animateWave(e.cm, t, 0.2);   // thanks for the lift
        } else {
          animateWalk(e.cm, t, 1.2);
          e.cm.root.position.addInPlace(e.leaveDir.scale(dt * 2.2));
        }
        if (e.leaveT > 2.6) this.remove(id);
      }
    }
  }
}
